import businessService from '@/modules/business/services/businessService';
import webAIWidgetApiService from '../services/webAIWidgetApiService';
import formatTimeSlotsByDayToHumanReadable from '../utils/businessHoursFormatter';
import { DEFAULT_CONFIG, FALLBACK_BUSINESS_NAME, getDefaultWelcomeMessage } from '../constants/webAIWidgetDefaults';

const URL_KEYS = ['logo_url'];
const OMIT_KEYS = ['id', 'uid', 'business_id', 'created_at', 'updated_at'];

/**
 * Portal host for the current environment (vitrage/client portal).
 * 1) From Frontage app_config.LivesiteUrl when POV is served inside Rails layout.
 * 2) Fallback: when hostname is frontage-<namespace>.external.int-eks.vchost.co, use vitrage-<namespace>.external.int-eks.vchost.co
 * @returns {string|undefined} Host only (e.g. vitrage-itzik.external.int-eks.vchost.co) or undefined
 */
function getPortalHostFromAppConfig() {
  if (typeof window === 'undefined' || !window.location) return undefined;

  const appConfig = window.app_config;
  if (appConfig && appConfig.LivesiteUrl && typeof appConfig.LivesiteUrl === 'string') {
    try {
      const url = appConfig.LivesiteUrl.startsWith('http') ? appConfig.LivesiteUrl : `https://${appConfig.LivesiteUrl}`;
      const parsed = new URL(url);
      if (parsed.host) return parsed.host;
    } catch {
      // fall through to hostname fallback
    }
  }

  const hostname = (window.location.hostname || '').toLowerCase();
  if (hostname.startsWith('frontage-') && hostname.includes('.external.int-eks.vchost.co')) {
    return 'vitrage-' + hostname.slice('frontage-'.length);
  }
  return undefined;
}

/** Keys the backend may not persist/return yet; we merge them from the sent payload after save so the UI does not reset. */
const GUIDANCE_KEYS = [
  'include_contact_info',
  'include_services',
  'include_products',
  'include_booking_policies',
  'include_terms_of_service',
  'guidance_urls',
  'faq_and_guidance',
  'business_hours',
];

/**
 * Build request payload for PUT main: snake_case, omit id/uid/business_id/dates,
 * empty URL fields as null.
 * @param {object} config - Full config (id, business_id, widget_title, actions, ...)
 * @param {string} [widgetTitle] - Override for widget_title
 * @returns {object} Payload for upsertMainConfig
 */
function buildRequestPayload(config, widgetTitle) {
  if (!config) return {};
  const out = { ...config };
  OMIT_KEYS.forEach((k) => delete out[k]);
  if (widgetTitle !== undefined) {
    out.widget_title = widgetTitle;
  }
  URL_KEYS.forEach((k) => {
    if (out[k] === '' || out[k] === undefined) {
      out[k] = null;
    }
  });
  if (!Array.isArray(out.guidance_urls)) {
    out.guidance_urls = out.guidance_urls != null ? [out.guidance_urls] : [];
  }
  const portalHost = getPortalHostFromAppConfig();
  if (portalHost) {
    out.portal_host = portalHost;
  }
  if (out.business_hours === '') {
    out.business_hours = null;
  }
  return out;
}

const state = {
  config: null,
  business: null,
  rawBusinessName: null,
  isLoading: false,
  error: null,
  /** Available client portal actions for this business (from API or placeholder). Shape: [{ key, defaultLabel }]. */
  availablePortalActions: [],
  historyRefreshTrigger: 0,
  historyGoToEditorTrigger: 0,
  editorSaveTrigger: 0,
  editorGoToHistoryTrigger: 0,
  editorShowEmbedTrigger: 0,
  editorOpenMoreMenuTrigger: 0,
  editorMoreSelectTrigger: { timestamp: 0, identifier: null },
  defaultBusinessHours: null,
  defaultBusinessHoursError: null,
};

const getters = {
  config: (s) => s.config,
  business: (s) => s.business,
  rawBusinessName: (s) => s.rawBusinessName,
  isLoading: (s) => s.isLoading,
  error: (s) => s.error,
  availablePortalActions: (s) => s.availablePortalActions,
  historyRefreshTrigger: (s) => s.historyRefreshTrigger,
  historyGoToEditorTrigger: (s) => s.historyGoToEditorTrigger,
  editorSaveTrigger: (s) => s.editorSaveTrigger,
  editorGoToHistoryTrigger: (s) => s.editorGoToHistoryTrigger,
  editorShowEmbedTrigger: (s) => s.editorShowEmbedTrigger,
  editorOpenMoreMenuTrigger: (s) => s.editorOpenMoreMenuTrigger,
  editorMoreSelectTrigger: (s) => s.editorMoreSelectTrigger,
  defaultBusinessHours: (s) => s.defaultBusinessHours,
  defaultBusinessHoursError: (s) => s.defaultBusinessHoursError,
};

const mutations = {
  setConfig(state, config) {
    state.config = config;
  },
  setBusiness(state, business) {
    state.business = business;
  },
  setRawBusinessName(state, name) {
    state.rawBusinessName = name;
  },
  setLoading(state, value) {
    state.isLoading = value;
  },
  setError(state, error) {
    state.error = error;
  },
  setAvailablePortalActions(state, portalActions) {
    state.availablePortalActions = Array.isArray(portalActions) ? portalActions : [];
  },
  historyRefreshTrigger(state) {
    state.historyRefreshTrigger = Date.now();
  },
  historyGoToEditorTrigger(state) {
    state.historyGoToEditorTrigger = Date.now();
  },
  editorSaveTrigger(state) {
    state.editorSaveTrigger = Date.now();
  },
  editorGoToHistoryTrigger(state) {
    state.editorGoToHistoryTrigger = Date.now();
  },
  editorShowEmbedTrigger(state) {
    state.editorShowEmbedTrigger = Date.now();
  },
  editorOpenMoreMenuTrigger(state) {
    state.editorOpenMoreMenuTrigger = Date.now();
  },
  setEditorMoreSelect(state, identifier) {
    state.editorMoreSelectTrigger = { timestamp: Date.now(), identifier };
  },
  setDefaultBusinessHours(state, value) {
    state.defaultBusinessHours = value;
    state.defaultBusinessHoursError = null;
  },
  setDefaultBusinessHoursError(state, message) {
    state.defaultBusinessHours = null;
    state.defaultBusinessHoursError = message;
  },
};

const actions = {
  async fetchAvailablePortalActions({ commit, rootGetters }) {
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) return;
    try {
      const list = await webAIWidgetApiService.getAvailablePortalActions(businessUid);
      commit('setAvailablePortalActions', list);
    } catch (_) {
      commit('setAvailablePortalActions', []);
    }
  },

  async fetchDefaultBusinessHours({ commit, rootGetters }) {
    try {
      const staffOwner = rootGetters['StaffStore/staffOwner'];
      const uid = staffOwner?.weekly_availability_uid
        && String(staffOwner.weekly_availability_uid).trim();
      if (!uid) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const data = await webAIWidgetApiService.getWeeklyAvailabilityGeneral(uid);
      const slots = data?.time_slots_by_day;
      if (!Array.isArray(slots) || slots.length === 0) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const formatted = formatTimeSlotsByDayToHumanReadable(slots);
      commit('setDefaultBusinessHours', formatted);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403
        ? 'web_ai_widget.content_tab.business_hours_unable_to_retrieve'
        : err?.message || null;
      commit('setDefaultBusinessHoursError', message);
    }
  },

  async fetchDefaults({ commit, state, rootGetters, dispatch }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      if (!businessUid) {
        commit('setError', 'Business context required');
        return;
      }
      await dispatch('StaffStore/fetchAllStaff', null, { root: true });
      await dispatch('fetchDefaultBusinessHours');
      await webAIWidgetApiService.getAvailablePortalActions(businessUid).then((list) => {
        commit('setAvailablePortalActions', list);
      }).catch(() => commit('setAvailablePortalActions', []));
      const result = await webAIWidgetApiService.getMainConfig(businessUid);
      let rawName = null;
      try {
        const data = await businessService.fetchBusiness(businessUid);
        rawName = data?.business?.business?.name || null;
      } catch (_) {
        // Ignore; rawName stays null
      }
      if (result.config !== null) {
        const loadedConfig = { ...result.config };
        if (!Array.isArray(loadedConfig.actions) || loadedConfig.actions.length === 0) {
          const availableActions = state.availablePortalActions || [];
          loadedConfig.actions = availableActions.map((a) => ({
            action: a.key,
            label: a.defaultLabel,
            availability: true,
          }));
        }
        commit('setConfig', loadedConfig);
        commit('setBusiness', result.business);
        if (rawName) commit('setRawBusinessName', rawName);
        return;
      }
      const widgetTitleDefault = rawName || result.business?.name || FALLBACK_BUSINESS_NAME;
      commit('setRawBusinessName', widgetTitleDefault);
      const brandColor = rootGetters['BusinessStore/businessSettingsBrandColor'];
      if (brandColor == null || brandColor === '') {
        commit('setError', 'web_ai_widget.error_business_color_unavailable');
        return;
      }
      const businessLogoUrl = rootGetters['BusinessStore/businessLogoUrl'] ?? '';
      const businessTextColor = rootGetters['BusinessStore/businessSettingsTextColor'];
      const availableActions = state.availablePortalActions || [];
      const actionsArray = availableActions.map((a) => ({
        action: a.key,
        label: a.defaultLabel,
        availability: true,
      }));
      const config = {
        ...DEFAULT_CONFIG,
        business_id: businessUid,
        widget_title: widgetTitleDefault,
        welcome_message: getDefaultWelcomeMessage(widgetTitleDefault),
        brand_color: brandColor,
        text_color: businessTextColor ?? DEFAULT_CONFIG.text_color,
        logo_url: businessLogoUrl,
        actions: actionsArray,
        business_hours: state.defaultBusinessHours ?? undefined,
      };
      const portalHost = getPortalHostFromAppConfig();
      if (portalHost) {
        config.portal_host = portalHost;
      }
      // Persist via PUT .../main so the widget uid exists before the editor preview loads (embed/public config).
      await dispatch('saveConfig', config);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403 ? 'web_ai_widget.error_forbidden' : err?.message || 'Failed to load';
      commit('setError', message);
    } finally {
      commit('setLoading', false);
    }
  },

  triggerHistoryRefresh({ commit }) {
    commit('historyRefreshTrigger');
  },
  triggerHistoryGoToEditor({ commit }) {
    commit('historyGoToEditorTrigger');
  },
  triggerEditorSave({ commit }) {
    commit('editorSaveTrigger');
  },
  triggerEditorGoToHistory({ commit }) {
    commit('editorGoToHistoryTrigger');
  },
  triggerEditorShowEmbed({ commit }) {
    commit('editorShowEmbedTrigger');
  },
  triggerEditorOpenMoreMenu({ commit }) {
    commit('editorOpenMoreMenuTrigger');
  },
  triggerEditorMoreSelect({ commit }, identifier) {
    commit('setEditorMoreSelect', identifier);
  },
  async saveConfig({ commit, state, rootGetters }, payload) {
    commit('setError', null);
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) {
      const err = new Error('Business context required');
      throw err;
    }
    const configSource = payload ?? state.config;
    const widgetTitle = configSource && Object.prototype.hasOwnProperty.call(configSource, 'widget_title')
      ? configSource.widget_title
      : state.business?.name;
    const requestPayload = buildRequestPayload(configSource, widgetTitle);
    const result = await webAIWidgetApiService.upsertMainConfig(
      businessUid,
      requestPayload,
    );
    const configToCommit = { ...result.config };
    GUIDANCE_KEYS.forEach((key) => {
      if (requestPayload[key] !== undefined) {
        configToCommit[key] = requestPayload[key];
      }
    });
    if (requestPayload.actions !== undefined) {
      configToCommit.actions = requestPayload.actions;
    }
    commit('setConfig', configToCommit);
    commit('setBusiness', result.business);
    return configToCommit;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
